import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import {
  useAllocationRun,
  useAllocationRuns,
  useCancelRun,
  usePeriods,
  useStartRun,
} from '../api/hooks'
import type { AllocationRun, RunStatus } from '../api/types'
import { formatMoney } from '../components/Badges'
import { formatDateTime } from '../components/formatTime'

/**
 * Screen 8 — Run Console (§10).
 *
 * Period selector, run button, preview toggle, live status, and run history with source
 * vs allocated totals and a reconciliation checkmark.
 */

const STATUS: Record<RunStatus, { label: string; bg: string; fg: string }> = {
  Queued: { label: 'Queued', bg: 'var(--color-panel-alt)', fg: 'var(--color-muted)' },
  QcRunning: { label: 'QC running', bg: 'var(--color-accent-wash)', fg: 'var(--color-accent)' },
  QcFailed: { label: 'QC failed', bg: 'var(--color-error-wash)', fg: 'var(--color-error)' },
  Resolving: { label: 'Resolving', bg: 'var(--color-accent-wash)', fg: 'var(--color-accent)' },
  Allocating: { label: 'Allocating', bg: 'var(--color-accent-wash)', fg: 'var(--color-accent)' },
  Completed: { label: 'Completed', bg: 'var(--color-ok-wash)', fg: 'var(--color-ok)' },
  Failed: { label: 'Failed', bg: 'var(--color-error-wash)', fg: 'var(--color-error)' },
  Cancelled: { label: 'Cancelled', bg: 'var(--color-panel-alt)', fg: 'var(--color-muted)' },
  Superseded: { label: 'Superseded', bg: 'var(--color-panel-alt)', fg: 'var(--color-muted)' },
}

export function RunConsole() {
  const { data: periods = [] } = usePeriods()
  const [periodId, setPeriodId] = useState<number | null>(null)
  const [isPreview, setIsPreview] = useState(true)
  const [activeRunId, setActiveRunId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const effectivePeriodId = periodId ?? periods[0]?.periodId ?? null
  const period = periods.find((p) => p.periodId === effectivePeriodId)

  const { data: runs = [] } = useAllocationRuns(effectivePeriodId)
  const { data: activeRun } = useAllocationRun(activeRunId)

  const start = useStartRun()
  const cancel = useCancelRun()
  const navigate = useNavigate()

  // A period with no balances has nothing to allocate — QC-P002's condition.
  const hasBalances = (period?.balanceCount ?? 0) > 0
  const periodBlocksRun = period?.status === 'Closed' || (period?.status === 'Locked' && !isPreview)

  const blockedReason = !effectivePeriodId
    ? 'Select a period.'
    : !hasBalances
      ? 'No balances are loaded for this period, so there is nothing to allocate.'
      : periodBlocksRun
        ? period?.status === 'Closed'
          ? 'The period is Closed. No run can be started against it.'
          : 'The period is Locked. Only a preview run is allowed.'
        : null

  function startRun() {
    if (!effectivePeriodId) return
    setError(null)

    start.mutate(
      { periodId: effectivePeriodId, isPreview },
      {
        onSuccess: (run) => setActiveRunId(run.runId),
        onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
      },
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        <h1 className="text-[14px] font-semibold">Run Console</h1>

        <select
          className="input w-40"
          value={effectivePeriodId ?? ''}
          onChange={(e) => {
            setPeriodId(Number(e.target.value))
            setActiveRunId(null)
          }}
        >
          {periods.map((p) => (
            <option key={p.periodId} value={p.periodId}>
              {p.periodCode} ({p.status})
            </option>
          ))}
        </select>

        <label className="flex items-center gap-1.5 text-[12px]">
          <input
            type="checkbox"
            checked={isPreview}
            onChange={(e) => setIsPreview(e.target.checked)}
          />
          Preview
          <span style={{ color: 'var(--color-faint)' }}>
            (never supersedes the period's results)
          </span>
        </label>

        <button
          className="btn btn-primary"
          onClick={startRun}
          disabled={start.isPending || blockedReason !== null}
          title={blockedReason ?? undefined}
        >
          {start.isPending ? 'Starting…' : isPreview ? 'Run preview' : 'Run allocation'}
        </button>

        {blockedReason && (
          <span className="text-[12px]" style={{ color: 'var(--color-warn)' }}>
            {blockedReason}
          </span>
        )}

        {period && (
          <span className="ml-auto text-[11px]" style={{ color: 'var(--color-faint)' }}>
            {period.balanceCount} balances · <span className="num">{formatMoney(period.totalBalanceAmount)}</span>
          </span>
        )}
      </div>

      {error && (
        <div
          className="border-b px-4 py-2 text-[12px]"
          style={{
            background: 'var(--color-error-wash)',
            color: 'var(--color-error)',
            borderColor: 'var(--color-rule-soft)',
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      {activeRun && !activeRun.isTerminal && (
        <div
          className="flex items-center gap-3 border-b px-4 py-2 text-[12px]"
          style={{
            background: 'var(--color-accent-wash)',
            color: 'var(--color-accent)',
            borderColor: 'var(--color-rule-soft)',
          }}
        >
          <span className="font-medium">Run {activeRun.runId} is {STATUS[activeRun.status].label.toLowerCase()}…</span>
          <button
            className="btn btn-sm"
            onClick={() => cancel.mutate(activeRun.runId)}
            disabled={cancel.isPending}
          >
            Cancel
          </button>
        </div>
      )}

      {activeRun?.status === 'Failed' && activeRun.errorMessage && (
        <div
          className="border-b px-4 py-2 text-[12px]"
          style={{
            background: 'var(--color-error-wash)',
            color: 'var(--color-error)',
            borderColor: 'var(--color-rule-soft)',
          }}
          role="alert"
        >
          <span className="font-medium">Run {activeRun.runId} failed. </span>
          {activeRun.errorMessage}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
            <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
              <th className="col-head px-3 py-1.5 text-right">Run</th>
              <th className="col-head px-3 py-1.5">Status</th>
              <th className="col-head px-3 py-1.5">Type</th>
              <th className="col-head px-3 py-1.5 text-right">Source</th>
              <th className="col-head px-3 py-1.5 text-right">Allocated</th>
              <th className="col-head px-3 py-1.5">Reconciled</th>
              <th className="col-head px-3 py-1.5 text-right">Rows</th>
              <th className="col-head px-3 py-1.5">Started</th>
              <th className="col-head px-3 py-1.5">By</th>
              <th className="px-3 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <RunRow
                key={run.runId}
                run={run}
                onSelect={() => setActiveRunId(run.runId)}
                onOpenResults={() => navigate(`/results/${run.runId}`)}
              />
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
                  No runs yet for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/*
          Schedules and alert recipients used to be rendered here, below the run list.

          They are standing instructions to do what the button above does, so this looked
          like the natural home — but they are configuration touched once, sitting under a
          screen watched daily, which is how a setting becomes unfindable. They live under
          Settings now. A line pointing there costs one row and saves the search.
      */}
      <p
        className="border-t px-4 py-2 text-[11px]"
        style={{ borderColor: 'var(--color-rule)', color: 'var(--color-faint)' }}
      >
        To run this unattended, set a schedule under{' '}
        <Link
          to="/settings/schedules"
          className="underline"
          style={{ color: 'var(--color-accent)' }}
        >
          Settings → Schedules
        </Link>
        , and choose who hears about a failure under{' '}
        <Link to="/settings/alerts" className="underline" style={{ color: 'var(--color-accent)' }}>
          Settings → Alerts
        </Link>
        .
      </p>
    </div>
  )
}

function RunRow({
  run,
  onSelect,
  onOpenResults,
}: {
  run: AllocationRun
  onSelect: () => void
  onOpenResults: () => void
}) {
  const status = STATUS[run.status]
  const finished = run.status === 'Completed' || run.status === 'Superseded'

  return (
    <tr
      className="grid-row border-b"
      style={{ borderColor: 'var(--color-rule-soft)' }}
      tabIndex={0}
      onClick={onSelect}
    >
      <td className="num px-3 py-1">{run.runId}</td>
      <td className="px-3 py-1">
        <span className="badge" style={{ background: status.bg, color: status.fg }}>
          {status.label}
        </span>
      </td>
      <td className="px-3 py-1" style={{ color: 'var(--color-muted)' }}>
        {run.isPreview ? 'Preview' : run.runType}
      </td>
      <td className="num px-3 py-1">
        {run.totalSourceAmount === null ? '—' : formatMoney(run.totalSourceAmount)}
      </td>
      <td className="num px-3 py-1">
        {run.totalAllocatedAmount === null ? '—' : formatMoney(run.totalAllocatedAmount)}
      </td>
      <td className="px-3 py-1">
        {finished ? (
          run.reconciles ? (
            <span style={{ color: 'var(--color-ok)' }} title="Allocated equals source, to the cent.">
              ✓
            </span>
          ) : (
            <span style={{ color: 'var(--color-error)' }} title="Allocated does not equal source.">
              ✗
            </span>
          )
        ) : (
          <span style={{ color: 'var(--color-faint)' }}>—</span>
        )}
      </td>
      <td className="num px-3 py-1" style={{ color: 'var(--color-muted)' }}>
        {run.resultRowCount}
      </td>
      <td className="ident px-3 py-1" style={{ color: 'var(--color-muted)' }}>
        {formatDateTime(run.startedUtc)}
      </td>
      <td className="px-3 py-1" style={{ color: 'var(--color-muted)' }}>
        {run.initiatedBy}
      </td>
      <td className="px-3 py-1 text-right">
        {run.resultRowCount > 0 && (
          <button
            className="text-[11px] underline"
            style={{ color: 'var(--color-accent)' }}
            onClick={(e) => {
              e.stopPropagation()
              onOpenResults()
            }}
          >
            results
          </button>
        )}
      </td>
    </tr>
  )
}
